import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader";
import { t } from "../../i18n";
import * as shared from "../../styles/shared";
import { fetchBackendApiCoverageSnapshot } from "./backend-api-coverage-fetch";
import { backendApiCoverageStyles as styles } from "./backend-api-coverage-styles";
import type { BackendApiCoverageSnapshot } from "./backend-api-coverage-types";
import { BackendApiTestsBrowser } from "./backend-api-tests-browser";

export function BackendApiTestsPage(): React.JSX.Element {
  const { filePath } = useParams();
  const [snapshot, setSnapshot] = useState<BackendApiCoverageSnapshot | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setSnapshot(await fetchBackendApiCoverageSnapshot());
    } catch (err) {
      setSnapshot(null);
      setError(
        err instanceof Error ? err.message : t("api_coverage.snapshot_failed"),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <PageHeader
        title={t("api_tests.title")}
        description={t("api_tests.description")}
        actions={
          <>
            <Link to="/system/backend-api-coverage" style={styles.pagerButton}>
              {t("api_tests.open_api_coverage")}
            </Link>
            <button type="button" onClick={load} style={shared.refreshBtn}>
              {t("common.refresh")}
            </button>
          </>
        }
      />
      {error && <p style={shared.errorBanner}>{error}</p>}
      {loading && <p style={shared.loadingText}>{t("common.loading")}</p>}
      {snapshot ? (
        <>
          <p style={styles.auditMeta}>
            {t("api_coverage.generated_at")}:{" "}
            {new Date(snapshot.generated_at).toLocaleString()}
          </p>
          <BackendApiTestsBrowser
            snapshot={snapshot}
            selectedFilePath={safeDecode(filePath)}
          />
        </>
      ) : null}
    </div>
  );
}

function safeDecode(value?: string): string | undefined {
  if (!value) return undefined;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
